import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppointmentAddOn, AppointmentAddOnInput, BASE_URL_TOKEN } from '@raptor-fe/core';

/**
 * API service for check-in add-ons. Head office manages the definitions once per tenant; each branch offers them
 * at its own store price, so the branch read and the tenant management calls live on different routes.
 */
@Injectable({
    providedIn: 'root',
})
export class AppointmentAddOnApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = inject(BASE_URL_TOKEN);

    /**
     * Retrieves the add-ons a branch can offer at check-in, each carrying that store's live price. Add-ons the
     * store does not stock are left out by the API.
     */
    getActiveAddOns(tenantId: string, branchId: string): Observable<AppointmentAddOn[]> {
        return this.http.get<AppointmentAddOn[]>(`${this.tenantUrl(tenantId)}/branch/${branchId}/appointment-add-ons/active`);
    }

    /**
     * Retrieves every add-on definition of the tenant, active and inactive, for head office management.
     */
    getAll(tenantId: string): Observable<AppointmentAddOn[]> {
        return this.http.get<AppointmentAddOn[]>(this.addOnsUrl(tenantId));
    }

    /**
     * Retrieves a single add-on definition.
     */
    getById(tenantId: string, id: string): Observable<AppointmentAddOn> {
        return this.http.get<AppointmentAddOn>(this.addOnsUrl(tenantId, id));
    }

    /**
     * Creates a tenant add-on definition.
     */
    create(tenantId: string, addOn: AppointmentAddOnInput): Observable<AppointmentAddOn> {
        return this.http.post<AppointmentAddOn>(this.addOnsUrl(tenantId), addOn);
    }

    /**
     * Updates a tenant add-on definition.
     */
    update(tenantId: string, id: string, addOn: AppointmentAddOnInput): Observable<AppointmentAddOn> {
        return this.http.put<AppointmentAddOn>(this.addOnsUrl(tenantId, id), addOn);
    }

    /**
     * Deactivates a tenant add-on definition.
     */
    delete(tenantId: string, id: string): Observable<void> {
        return this.http.delete<void>(this.addOnsUrl(tenantId, id));
    }

    private tenantUrl(tenantId: string): string {
        return `${this.baseUrl}v1/tenant/${tenantId}`;
    }

    private addOnsUrl(tenantId: string, path = ''): string {
        const base = `${this.tenantUrl(tenantId)}/appointment-add-ons`;
        return path ? `${base}/${path}` : base;
    }
}
